import React, { useMemo, useRef } from 'react';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import WhatsAppSettings from './WhatsAppSettings';
import WhatsAppTemplates from './WhatsAppTemplates';
import { WhatsAppSettingsProvider } from './WhatsAppSettingsContext';

const TAB_SETUP = 'setup';
const TAB_TEMPLATES = 'templates';

export default function WhatsAppConfiguration() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tab === TAB_TEMPLATES ? TAB_TEMPLATES : TAB_SETUP;
  }, [searchParams]);

  const saveBeforeTemplatesRef = useRef(null);

  const handleTabChange = async (_event, value) => {
    if (value === TAB_TEMPLATES && saveBeforeTemplatesRef.current) {
      const result = await saveBeforeTemplatesRef.current();
      if (result?.ok && !result?.skipped) {
        toast.success(
          t('whatsappTemplates.connectionSaved', {
            defaultValue: 'Connection saved. You can add message templates now.',
          })
        );
      } else if (result?.ok === false && result?.error) {
        toast.warning(result.error);
      }
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <WhatsAppSettingsProvider>
    <Box sx={{ px: { xs: 1.5, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1, pt: 1 }}
        >
          <Tab value={TAB_SETUP} label={t('whatsappSettings.tabs.connection')} />
          <Tab value={TAB_TEMPLATES} label={t('whatsappSettings.tabs.messageTemplates')} />
        </Tabs>
      </Paper>

      <Box sx={{ display: activeTab === TAB_SETUP ? 'block' : 'none' }}>
        <WhatsAppSettings
          registerSaveBeforeTemplates={(fn) => {
            saveBeforeTemplatesRef.current = fn;
          }}
        />
      </Box>
      <Box sx={{ display: activeTab === TAB_TEMPLATES ? 'block' : 'none' }}>
        <WhatsAppTemplates />
      </Box>
    </Box>
    </WhatsAppSettingsProvider>
  );
}
